import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Tooltip,
  Dialog,
  Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '../services/projectService';
import { clientService } from '../services/clientService';
import ProjectForm from '../components/projects/ProjectForm';
import { Project, CreateProjectData, UpdateProjectData } from '../types';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useProjectWorkflow } from '../contexts/ProjectWorkflowContext';
import CheckoutModal from '../components/cart/CheckoutModal';
import ArchiveIconSvg from '../components/common/ArchiveIconSvg';
import { useNavigate } from 'react-router-dom';
import {
  completeProjectCreation,
  completeProjectDeletion,
  completeProjectUpdate,
} from '../utils/workflowPanelCache';

const ProjectsPage: React.FC = () => {
  const { user, isAdmin, isSuperAdmin, isAgency, isClient, hasPermission } = useAuth();
  const {
    focusClientInPanel,
    selectProject,
    clearProject,
    selectedProject: workflowProject,
    patchProjectInSelection,
  } = useProjectWorkflow();
  const canManageHistoricalUsage = isSuperAdmin || hasPermission('historical_usage');
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | undefined>();
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);
  const [checkoutProject, setCheckoutProject] = useState<Project | undefined>();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const queryClient = useQueryClient();

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { page: page + 1, limit: rowsPerPage, search, clientId: clientFilter }],
    queryFn: () => projectService.getProjects({ 
      page: page + 1, 
      limit: rowsPerPage, 
      search,
      clientId: clientFilter || undefined 
    }),
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients'],
    queryFn: () => clientService.getClients({ limit: 1000 }),
    enabled: !isClient, // Only fetch clients for Admin users, not CLIENT users
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateProjectData) => projectService.createProject(data),
    onSuccess: (project) => {
      const clientId = project.clientId || project.client?.id || '';
      const clientName =
        project.client?.name ||
        clientsData?.data?.find((c) => c.id === clientId)?.name ||
        'Client';
      completeProjectCreation(queryClient, project, {
        closeDialog: () => {
          setFormOpen(false);
          setSelectedProject(undefined);
        },
        focusClientInPanel,
        selectProject,
        clientName: isClient ? 'My account' : clientName,
      });
      const projectName = project.projectName || 'Project';
      setSnackbar({ open: true, message: `A new project '${projectName}' has been added.` });
    },
    onError: (error: any) => {
      console.error('Create project error:', error);
      // Let the form handle the error display
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProjectData }) =>
      projectService.updateProject(id, data),
    onSuccess: (updated) => {
      const clientName =
        updated.client?.name ||
        clientsData?.data?.find((c) => c.id === (updated.clientId || updated.client?.id))?.name;
      completeProjectUpdate(queryClient, updated, {
        patchProjectInSelection,
        clientName,
      });
      setFormOpen(false);
      setSelectedProject(undefined);
      setSnackbar({
        open: true,
        message: `Project '${updated.projectName}' updated successfully.`,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => projectService.deleteProject(id),
    onSuccess: (_data, projectId) => {
      completeProjectDeletion(queryClient, projectId, {
        clearProject,
        selectedProjectId: workflowProject?.id,
        onCleared: () => navigate('/projects'),
      });
      setDeleteDialogOpen(false);
      setProjectToDelete(undefined);
      setSnackbar({ open: true, message: 'Project archived successfully.' });
    },
    onError: (error: any) => {
      console.error('Delete project error:', error);
      // Keep dialog open to show error message
    },
  });

  
  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setFormOpen(true);
  };

  const handleDeleteProject = (project: Project) => {
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleProjectOverview = (project: Project) => {
    const clientId = project.clientId || project.client?.id || '';
    const clientName =
      project.client?.name ||
      clientsData?.data?.find((c) => c.id === clientId)?.name ||
      (isClient ? 'My account' : 'Client');
    selectProject(project, clientName, 0, 'overview', { from: 'projects' });
  };

  const handleFormSubmit = async (data: CreateProjectData | UpdateProjectData) => {
    if (selectedProject) {
      await updateMutation.mutateAsync({ id: selectedProject.id, data });
    } else {
      await createMutation.mutateAsync(data as CreateProjectData);
    }
  };

  const handleDeleteConfirm = async () => {
    if (projectToDelete) {
      try {
        await deleteMutation.mutateAsync(projectToDelete.id);
      } catch (error) {
        // Error is handled by the mutation's onError callback
        console.error('Delete failed:', error);
      }
    }
  };

  const handleBrowseSites = (project: Project) => {
    navigate(`/sites?projectId=${project.id}&clientId=${project.client?.id}`);
  };

  
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const projects = projectsData?.data || [];
  const totalCount = projectsData?.pagination?.total || 0;
  const clients = clientsData?.data || [];

  return (
    <Box sx={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Page Header */}
      <Box>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.5px', mb: 0.5 }}>
          Projects
        </Typography>
        <Typography sx={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          {projectsData?.pagination?.total || projects?.length || 0} active projects across all clients
        </Typography>
      </Box>

      {/* Stats Row */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '14px', 
        mb: 1 
      }}>
        <Box sx={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)'
          }
        }}>
          <Box sx={{
            width: '36px',
            height: '36px',
            borderRadius: '9px',
            background: 'var(--accent-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--accent)' }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {projectsData?.pagination?.total || projects?.length || 0}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Total Projects
            </Box>
          </Box>
        </Box>

        <Box sx={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)'
          }
        }}>
          <Box sx={{
            width: '36px',
            height: '36px',
            borderRadius: '9px',
            background: 'var(--green-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--green)' }}>
              <circle cx="9" cy="21" r="1"/>
              <circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {projects?.reduce((sum, p) => sum + ((p as any).cartCount || 0), 0) || 0}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Active Orders
            </Box>
          </Box>
        </Box>

        <Box sx={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)'
          }
        }}>
          <Box sx={{
            width: '36px',
            height: '36px',
            borderRadius: '9px',
            background: 'var(--amber-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--amber)' }}>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              {projects?.reduce((sum, p) => sum + ((p as any).placementCount || 0), 0) || 0}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Live Placements
            </Box>
          </Box>
        </Box>

        <Box sx={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '16px 18px',
          boxShadow: 'var(--shadow)',
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)'
          }
        }}>
          <Box sx={{
            width: '36px',
            height: '36px',
            borderRadius: '9px',
            background: 'var(--purple-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--purple)' }}>
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </Box>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1 }}>
              ${projects?.reduce((sum, p) => sum + ((p as any).projectBudget || p.budget ? Number((p as any).projectBudget || p.budget) : 0), 0).toLocaleString()}
            </Box>
            <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Total Budget / mo
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '8px 14px',
            flex: 1,
            maxWidth: '360px',
            transition: 'all 0.15s',
            boxShadow: 'var(--shadow)',
            '&:focus-within': {
              borderColor: 'var(--accent)',
              boxShadow: '0 0 0 3px rgba(37,99,235,0.08)'
            }
          }}>
            <SearchIcon sx={{ width: '14px', height: '14px', color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder="Search projects…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                border: 'none',
                background: 'none',
                outline: 'none',
                fontFamily: 'inherit',
                fontSize: '13px',
                color: 'var(--text-primary)',
                width: '100%'
              }}
            />
          </Box>
          
          {!isClient && (
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              style={{
                appearance: 'none',
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: '8px 32px 8px 12px',
                fontFamily: 'inherit',
                fontSize: '13px',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: 'var(--shadow)',
                minWidth: '160px',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239298a8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 10px center',
                              }}
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          )}
          
          <Button
            onClick={() => {
              setSearch('');
              setClientFilter('');
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'inherit',
              fontSize: '13px',
              color: 'var(--accent)',
              fontWeight: 500,
              cursor: 'pointer',
              background: 'var(--accent-soft)',
              border: '1px solid #c7d7fd',
              transition: 'all 0.15s',
              '&:hover': {
                background: '#dbeafe'
              }
            }}
          >
            <svg style={{ width: '13px', height: '13px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Clear Filters
          </Button>
        </Box>
      </Box>

      <Box sx={{
        width: '100%',
        overflow: 'hidden',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)',
        animation: 'fadeUp 0.35s ease forwards'
      }}>
        <TableContainer>
          <Table sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Project Name</TableCell>
                {!isClient && (
                  <TableCell sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    padding: '12px 18px',
                    borderBottom: '1px solid var(--border-light)',
                    background: 'var(--bg)',
                    whiteSpace: 'nowrap'
                  }}>Client</TableCell>
                )}
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Budget</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Placements</TableCell>
                <TableCell sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  padding: '12px 18px',
                  borderBottom: '1px solid var(--border-light)',
                  background: 'var(--bg)',
                  whiteSpace: 'nowrap'
                }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects.map((project) => (
                <TableRow 
                  hover 
                  key={project.id}
                  sx={{
                    transition: 'background 0.1s',
                    '&:hover': {
                      background: '#fafbff'
                    }
                  }}
                >
                  <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                      {project.projectName}
                    </Box>
                    <Box sx={{ fontSize: '11.5px', color: 'var(--text-muted)', mt: 0.5, fontFamily: 'DM Mono, monospace' }}>
                      {format(new Date(project.createdAt), 'MMM dd, yyyy')}
                    </Box>
                  </TableCell>
                  {!isClient && (
                    <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                      {project.client?.name ? (
                        <Box sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '3px 10px',
                          borderRadius: '99px',
                          fontSize: '12px',
                          fontWeight: 500,
                          background: '#fff0f5',
                          color: '#e11d48',
                          border: '1px solid #fecdd3',
                          whiteSpace: 'nowrap'
                        }}>
                          {project.client.name}
                        </Box>
                      ) : (
                        <Box sx={{ color: 'var(--text-muted)' }}>—</Box>
                      )}
                    </TableCell>
                  )}
                  <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    {(() => {
                      const budget = (project as any).projectBudget || (project.budget ? Number(project.budget) : 0);
                      const monthlySpent = (project as any).monthlySpent || 0;
                      const remaining = (project as any).monthlyBudgetRemaining;
                      if (!budget) {
                        return <Box sx={{ color: 'var(--text-muted)' }}>—</Box>;
                      }
                      const isOver = remaining !== null && remaining < 0;
                      return (
                        <Tooltip title={`Monthly Budget: $${budget.toFixed(0)} | Spent this month: $${monthlySpent.toFixed(0)}`}>
                          <Box>
                            <Box sx={{ fontWeight: 500, fontSize: '13px', color: 'var(--text-primary)', fontFamily: 'DM Mono, monospace' }}>
                              ${budget.toFixed(0)}/mo
                            </Box>
                            {remaining !== null && (
                              <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '2px 8px',
                                borderRadius: '99px',
                                fontSize: '11px',
                                fontWeight: 600,
                                marginTop: '3px',
                                fontFamily: 'DM Mono, monospace',
                                background: isOver ? 'var(--red-soft)' : remaining < budget * 0.2 ? 'var(--amber-soft)' : 'var(--green-soft)',
                                color: isOver ? 'var(--red)' : remaining < budget * 0.2 ? 'var(--amber)' : 'var(--green)',
                                border: isOver ? '1px solid var(--red-border)' : remaining < budget * 0.2 ? '1px solid var(--amber-border)' : '1px solid var(--green-border)'
                              }}>
                                {isOver ? `Over $${Math.abs(remaining).toFixed(0)}` : `$${remaining.toFixed(0)} left`}
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  </TableCell>
                  <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      {/* Placements Info */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: ((project as any).placementCount || 0) > 0 ? 'var(--text-secondary)' : 'var(--text-muted)'
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: ((project as any).placementCount || 0) > 0 ? 'var(--green)' : 'var(--text-muted)' }}>
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        {(project as any).placementCount || 0}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <Tooltip title="Project Overview">
                        <IconButton
                          size="small"
                          onClick={() => handleProjectOverview(project)}
                          sx={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            transition: 'all 0.15s',
                            '&:hover': {
                              background: 'var(--accent-soft)',
                            },
                            '&:hover svg': {
                              color: 'var(--accent)',
                            },
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                            <rect x="3" y="3" width="7" height="7" rx="1" />
                            <rect x="14" y="3" width="7" height="7" rx="1" />
                            <rect x="3" y="14" width="7" height="7" rx="1" />
                            <rect x="14" y="14" width="7" height="7" rx="1" />
                          </svg>
                        </IconButton>
                      </Tooltip>

                      {false && (
                      <Tooltip title="Select Site">
                        <IconButton
                          size="small"
                          onClick={() => handleBrowseSites(project)}
                          sx={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            transition: 'all 0.15s',
                            '&:hover': {
                              background: 'var(--accent-soft)'
                            },
                            '&:hover svg': {
                              color: 'var(--accent)'
                            }
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                            <line x1="12" y1="5" x2="12" y2="19"/>
                            <line x1="5" y1="12" x2="19" y2="12"/>
                          </svg>
                        </IconButton>
                      </Tooltip>
                      )}

                      {canManageHistoricalUsage && (
                        <Tooltip title="Historical Usage Upload">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/projects/${project.id}/historical-usage`)}
                            sx={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              border: 'none',
                              background: 'none',
                              transition: 'all 0.15s',
                              '&:hover': {
                                background: 'var(--blue-soft)'
                              },
                              '&:hover svg': {
                                color: 'var(--blue)'
                              }
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="17 8 12 3 7 8"/>
                              <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {false && ((project as any).cartCount || 0) > 0 && (
                        <Tooltip title="View Cart">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/orders?projectId=${project.id}&clientId=${project.client?.id}`)}
                            sx={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              border: 'none',
                              background: 'none',
                              transition: 'all 0.15s',
                              '&:hover': {
                                background: 'var(--blue-soft)'
                              },
                              '&:hover svg': {
                                color: 'var(--blue)'
                              }
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                              <circle cx="9" cy="21" r="1"/>
                              <circle cx="20" cy="21" r="1"/>
                              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                            </svg>
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {false && ((project as any).placementCount || 0) > 0 && (
                        <Tooltip title="View Placements">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              const currentMonth = new Date().getMonth() + 1;
                              const currentYear = new Date().getFullYear();
                              navigate(`/guest-blog-placements?projectId=${project.id}&clientId=${project.clientId}&month=${currentMonth}&year=${currentYear}`);
                            }}
                            sx={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              border: 'none',
                              background: 'none',
                              transition: 'all 0.15s',
                              '&:hover': {
                                background: 'var(--green-soft)'
                              },
                              '&:hover svg': {
                                color: 'var(--green)'
                              }
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                              <circle cx="12" cy="10" r="3"/>
                            </svg>
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <Tooltip title="Edit Project">
                        <IconButton
                          size="small"
                          onClick={() => handleEditProject(project)}
                          sx={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            transition: 'all 0.15s',
                            '&:hover': {
                              background: 'var(--amber-soft)'
                            },
                            '&:hover svg': {
                              color: 'var(--amber)'
                            }
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Archive Project">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteProject(project)}
                          sx={{
                            width: '30px',
                            height: '30px',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            border: 'none',
                            background: 'none',
                            transition: 'all 0.15s',
                            '&:hover': {
                              background: 'var(--amber-soft)'
                            },
                            '&:hover svg': {
                              color: 'var(--amber)'
                            }
                          }}
                        >
                          <ArchiveIconSvg />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '14px',
          padding: '14px 18px',
          borderTop: '1px solid var(--border-light)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12.5px', color: 'var(--text-secondary)' }}>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => handleChangeRowsPerPage(e)}
              style={{
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                borderRadius: '6px',
                padding: '3px 6px',
                fontFamily: 'inherit',
                fontSize: '12px',
                outline: 'none'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </Box>
          <Box sx={{ fontSize: '12.5px', color: 'var(--text-muted)' }}>
            {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, totalCount)} of {totalCount}
          </Box>
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': {
                  background: 'var(--bg)'
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: 'var(--text-secondary)' }}>
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
              sx={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': {
                  background: 'var(--bg)'
                },
                '&:disabled': {
                  opacity: 0.5,
                  cursor: 'not-allowed'
                }
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '13px', height: '13px', color: 'var(--text-secondary)' }}>
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      <ProjectForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedProject(undefined);
        }}
        onSubmit={handleFormSubmit}
        project={selectedProject}
        clients={clients}
        loading={createMutation.isPending || updateMutation.isPending}
        isClientUser={isClient}
        userClientId={user?.clientId || ''}
        canCreateClient={isAdmin || isAgency}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          deleteMutation.reset(); // Reset error state
        }}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
            border: '1px solid var(--border)',
            maxWidth: '560px',
            width: '100%'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '22px 26px 18px', borderBottom: '1px solid var(--border-light)' }}>
          <Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 600, letterSpacing: '-0.3px' }}>
              Archive Project
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: 'var(--text-muted)', mt: 0.5 }}>
              Hidden from lists — restore from the <strong>Archive</strong> menu
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setDeleteDialogOpen(false);
              deleteMutation.reset();
            }}
            sx={{
              width: '30px',
              height: '30px',
              borderRadius: '6px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s',
              flexShrink: 0,
              mt: 0.5,
              '&:hover': {
                background: 'var(--bg)'
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }}>
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </IconButton>
        </Box>
        <Box sx={{ padding: '22px 26px' }}>
          <Typography sx={{ fontSize: '14px', color: 'var(--text-primary)', mb: 2 }}>
            Archive project <strong>"{projectToDelete?.projectName}"</strong>?
            <br />
            It will be hidden from the sidebar and project lists. You can restore it within 7 days from{' '}
            <strong>Settings → Restore Archived Data</strong>.
          </Typography>
          {deleteMutation.error && (
            <Alert severity="error" sx={{ 
              mt: 2,
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px'
            }}>
              {deleteMutation.error?.response?.data?.error || 'Failed to archive project. Please try again.'}
            </Alert>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', padding: '16px 26px', borderTop: '1px solid var(--border-light)' }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              deleteMutation.reset();
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'inherit',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border)',
              '&:hover': {
                background: 'var(--bg)',
                color: 'var(--text-primary)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isPending}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontFamily: 'inherit',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              border: 'none',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              background: 'var(--amber)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(217,119,6,0.2)',
              '&:hover': {
                background: '#b45309',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)'
              },
              '&:disabled': {
                opacity: 0.6,
                cursor: 'not-allowed'
              }
            }}
          >
            {deleteMutation.isPending ? 'Archiving...' : 'Archive Project'}
          </Button>
        </Box>
      </Dialog>

      {checkoutProject && (
        <CheckoutModal
          open={checkoutModalOpen}
          onClose={() => {
            setCheckoutModalOpen(false);
            setCheckoutProject(undefined);
            queryClient.invalidateQueries({ queryKey: ['projects'] });
          }}
          projectId={checkoutProject.id}
          projectName={checkoutProject.projectName}
          clientName={checkoutProject.client?.name || ''}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ open: false, message: '' })} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectsPage;
